import { Bell } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRouter } from "@tanstack/react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/hooks/useNotifications";
import { useMarkNotificationRead } from "@/hooks/useMarkNotificationRead";
import { toast } from "@/utils/toast";
import type { Notification } from "@/hooks/types";

export function NotificationBell() {
  const { t } = useTranslation(["endUser", "common"]);
  const { isImpersonating } = useAuth();
  const { notifications, loading, error } = useNotifications();
  const { markRead } = useMarkNotificationRead();
  const router = useRouter();

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  // No bell while viewing as someone else. It could only ever show the
  // admin's own inbox — the rules keep `users/{uid}/notifications` own-doc-only
  // — and an admin's notifications rendered under the target's name is
  // exactly the confusion this session model is meant to end. An empty bell
  // would be its own lie ("they have none"), so it is absent instead.
  if (isImpersonating) return null;

  // Say so when it fails: a rejection dropped on the floor makes the button
  // look inert — the dot just stays. A silent write is worse than a visible
  // failure.
  const onMarkRead = (id: string) => {
    void markRead(id, true).catch(() =>
      toast.warning(t("endUser:notifications.markReadFailed")),
    );
  };

  // Following a notice is the strongest form of having read it, so the click
  // does both — and never blocks the navigation on the write. `history.push`
  // rather than typed navigation, because the path arrives as data from the
  // notification document.
  const onFollow = (notification: Notification) => {
    if (!notification.isRead) onMarkRead(notification.id);
    if (notification.linkUrl) router.history.push(notification.linkUrl);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          label={t("endUser:notifications.title")}
          className="relative"
        >
          <Bell />
          {unreadCount > 0 && (
            <span
              aria-label={t("endUser:notifications.unread", {
                count: unreadCount,
              })}
              className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-xs text-destructive-foreground"
            >
              {unreadCount}
            </span>
          )}
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        {/* Three answers, not one. The bell is most often opened in the first
            moments after a page load — the read is still in flight then, and
            "you have none" is a claim the hook has not made yet. A refused read
            is the same shape and just as wrong, and because the unread badge is
            derived from this same list it is absent too, so nothing else on
            screen hints that anything went wrong. */}
        {loading ? (
          <p className="p-3 text-sm text-muted-foreground">
            {t("endUser:notifications.loading")}
          </p>
        ) : error ? (
          <p className="p-3 text-sm text-muted-foreground">
            {t("endUser:notifications.unavailable")}
          </p>
        ) : notifications.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">
            {t("endUser:notifications.none")}
          </p>
        ) : (
          <ul className="max-h-80 overflow-auto">
            {notifications.map((n) => (
              <li
                key={n.id}
                className="flex items-start justify-between gap-2 border-b border-border p-3 text-sm last:border-b-0"
              >
                {n.linkUrl ? (
                  // A real menu item, so selecting it closes the menu; the
                  // page it announces is where the reader is headed anyway.
                  <DropdownMenuItem
                    className="min-w-0 flex-1 cursor-pointer flex-col items-start p-0 focus:bg-transparent"
                    onSelect={() => onFollow(n)}
                    data-testid={`notification-link-${n.id}`}
                  >
                    <p className={n.isRead ? "font-normal" : "font-medium"}>
                      {n.title}
                    </p>
                    {n.message && (
                      <p className="text-muted-foreground">{n.message}</p>
                    )}
                  </DropdownMenuItem>
                ) : (
                  <div className="min-w-0 flex-1">
                    <p className={n.isRead ? "font-normal" : "font-medium"}>
                      {n.title}
                    </p>
                    {n.message && (
                      <p className="text-muted-foreground">{n.message}</p>
                    )}
                  </div>
                )}
                {/* Kept beside the link: dismissing without following is a
                    legitimate way to read a notification. */}
                {!n.isRead && (
                  <Button
                    variant="ghost"
                    className="h-auto shrink-0 px-2 py-1 text-xs"
                    onClick={() => onMarkRead(n.id)}
                    data-testid={`button-mark-read-${n.id}`}
                  >
                    {t("endUser:notifications.markRead")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
